import React from "react";
import Seo from "../components/Seo";

const H2 = ({ children }) => (
  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-12 mb-5">{children}</h2>
);

export default function PanneauPvtAirPage() {
  return (
    <main data-testid="panneau-pvt-air-page" className="bg-white">
      <Seo
        title="Panneau PVT air 550 Wc | Panneau solaire hybride air | Thermasol Air Systems®"
        description="Panneau PVT air Thermasol 550 : panneau solaire hybride photovoltaïque et thermique, 550 Wc électriques et 1 397 Wth, radiateur aluminium en sous-face, certifié Solar Keymark, IEC 61215 et IEC 61730. Sans glycol."
        canonical="https://thermasol-airsystems.fr/panneau-pvt-air/"
        ogImage="https://thermasol-airsystems.fr/images/seo/panneau.webp"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Panneau PVT air Thermasol 550",
            brand: { "@type": "Brand", name: "Thermasol Air Systems" },
            image: "https://thermasol-airsystems.fr/images/seo/panneau.webp",
            description:
              "Panneau solaire hybride photovoltaïque et thermique : 550 Wc électriques et 1 397 Wth, radiateur aluminium en sous-face, certifié Solar Keymark, IEC 61215 et IEC 61730. Sans glycol.",
            offers: {
              "@type": "Offer",
              url: "https://thermasol-airsystems.fr/panneau-pvt-air",
              priceCurrency: "EUR",
              price: "0",
              priceSpecification: {
                "@type": "PriceSpecification",
                priceCurrency: "EUR",
                price: "0",
                description: "Prix sur devis selon dimensionnement",
              },
              availability: "https://schema.org/InStock",
              seller: { "@type": "Organization", name: "Thermasol Air Systems" },
              areaServed: "Europe",
            },
          },
        ]}
      />

      <section className="pt-28 pb-14 bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight">
              Panneau PVT air Thermasol 550 : électricité + air chaud
            </h1>
            <p className="text-slate-300 mt-6 leading-relaxed">
              Le panneau PVT air Thermasol 550 est un panneau solaire hybride photovoltaïque et thermique : sa face
              avant produit de l'électricité, son échangeur de chaleur en face arrière réchauffe le flux d'air circulant
              en sous-face. Technologie déposée INPI, sans fluide caloporteur, sans glycol — la seule architecture conforme
              à la lettre à la fiche CEE AGRI-EQ-110.
            </p>
          </div>
          <img src="/images/seo/panneau.webp" alt="panneau PVT air Thermasol 550 séchoir solaire radiateur aluminium sous-face" className="rounded-2xl shadow-2xl w-full object-cover" width="1264" height="848" />
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 md:px-6 pb-20">
        <H2>Caractéristiques techniques du panneau PVT air Thermasol 550</H2>
        <div className="grid sm:grid-cols-2 gap-5">
          {[
            ["Puissance électrique", "550 Wc"],
            ["Puissance thermique", "1 397 Wth"],
            ["Échangeur thermique", "Radiateur aluminium en face arrière (flux d'air en sous-face du panneau)"],
            ["Fluide", "Air uniquement — sans glycol, sans fluide caloporteur"],
            ["Certifications", "Solar Keymark · IEC 61215 · IEC 61730"],
            ["Température de soufflage", "Séchage basse température 25-40 °C"],
          ].map(([k, v]) => (
            <div key={k} className="bg-slate-50 border border-slate-200 rounded-xl p-5">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-bold">{k}</p>
              <p className="text-slate-800 font-semibold mt-1">{v}</p>
            </div>
          ))}
        </div>

        <H2>Un fonctionnement en sous-face unique</H2>
        <p className="text-slate-700 leading-relaxed">
          L'air ambiant est aspiré et circule en sous-face de chaque panneau, au contact direct du radiateur aluminium
          qui récupère la chaleur des cellules photovoltaïques. L'air se réchauffe de 15 à 20 °C avant d'être insufflé
          dans le bâtiment de séchage par les ventilateurs, via la chambre d'aspiration et la chambre de compression.
          Ce refroidissement des cellules améliore par ailleurs le rendement électrique du panneau.
        </p>
        <p className="text-slate-700 leading-relaxed mt-4">
          Cette architecture à échangeur de chaleur face arrière est au cœur du{" "}
          <a href="/sechoir-solaire" className="text-orange-600 font-semibold underline" data-testid="link-sechoir-solaire-panneau">séchoir solaire</a>{" "}
          Thermasol Air Systems®, éligible à la{" "}
          <a href="/fiche-cee-agri-eq-110" className="text-orange-600 font-semibold underline" data-testid="link-cee-panneau">prime CEE AGRI-EQ-110</a>.
          Thermasol Air Systems® assure la distribution européenne exclusive du panneau Thermasol 550.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <a href="/sechoir-solaire" className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-8 py-4 rounded-full text-center transition-colors" data-testid="cta-sechoir-panneau">
            Voir le séchoir solaire complet
          </a>
          <a href="/sechoir-solaire#etude" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-full text-center transition-colors" data-testid="cta-etude-panneau">
            Demander mon étude gratuite
          </a>
        </div>
      </div>
    </main>
  );
}
